// src/services/GraphService.js
//
// Directory data, now served by Vero's own backend rather than fetched from
// Microsoft Graph in the browser.
//
// The previous version called graph.microsoft.com directly with a delegated
// User.Read.All token. That meant every user who signed in consented to reading
// the entire tenant directory, and the filtering of service accounts happened
// here in JavaScript, so it was cosmetic rather than a boundary -- anyone could
// open devtools and read the unfiltered list.
//
// The backend now does the fetching with application permissions, so the broad
// scope lives in one service principal instead of on every employee's machine.
// The component API below is unchanged; only the source moved.

import apiClient from './apiClient'

const COUNTRY_NAMES = {
  DK: 'Denmark',
  SG: 'Singapore',
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Germany',
  SE: 'Sweden',
  NO: 'Norway',
  NL: 'Netherlands',
  PL: 'Poland',
  PK: 'Pakistan',
  IN: 'India',
  VN: 'Vietnam'
}

export function countryName(code) {
  return COUNTRY_NAMES[code] ?? code
}

export async function getSiteHierarchy() {
  const response = await apiClient.get('/GetSiteHierarchy')

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not load the site directory')
  }

  return {
    countries: (response.data.countries ?? []).map((c) => ({
      code: c.code,
      name: countryName(c.code),
      peopleWithoutSite: c.people_without_site ?? 0,
      sites: (c.sites ?? []).map((s) => ({ name: s.name, headcount: s.headcount }))
    })),
    stats: {
      total: response.data.stats?.total ?? 0,
      excluded: response.data.stats?.excluded ?? 0,
      unassigned: response.data.stats?.unassigned ?? 0,
      withoutOffice: response.data.stats?.without_office ?? 0,
      placed: response.data.stats?.placed ?? 0
    }
  }
}

/**
 * Everyone in a country, whether or not they have an office set in Entra.
 *
 * getPeopleAtSite matches on the office name, so a blank one made a person
 * invisible. The office is meant to show where somebody sits when the door
 * system has nothing to say about them; it was never supposed to decide whether
 * they exist.
 */
export async function getPeopleInCountry(countryCode) {
  const response = await apiClient.post('/GetPeopleInCountry', { country: countryCode })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not load the roster')
  }

  return (response.data.people ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    email: p.email,
    department: p.department || null,
    jobTitle: p.job_title || null,
    // Null rather than an empty string, so callers can test it plainly.
    office: p.office || null,
    managerId: p.manager_id || null
  }))
}

export async function getPeopleAtSite(countryCode, officeName) {
  const response = await apiClient.post('/GetPeopleAtSite', {
    country: countryCode,
    office: officeName
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not load the roster')
  }

  return (response.data.people ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    email: p.email,
    department: p.department || null,
    jobTitle: p.job_title || null,
    managerId: p.manager_id || null
  }))
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

// One request per person, proxied through the backend since the browser no
// longer holds a Graph token. A 404 is the normal answer for anyone without a
// mailbox or without an uploaded picture, so it is not surfaced as an error.

const photoCache = new Map()   // userId -> objectURL | null

async function fetchPhoto(userId) {
  if (photoCache.has(userId)) return photoCache.get(userId)

  try {
    const response = await apiClient.post(
      '/GetUserPhoto',
      { user_id: userId },
      { responseType: 'blob' }
    )

    const url = URL.createObjectURL(response.data)
    photoCache.set(userId, url)
    return url
  } catch {
    photoCache.set(userId, null)
    return null
  }
}

/**
 * Fetch photos for a list of user ids, a few at a time.
 *
 * The concurrency cap keeps a large site from opening forty simultaneous
 * requests; results are cached for the session so revisiting costs nothing.
 *
 * onPhoto is called as each arrives so faces appear progressively rather than
 * the whole grid waiting on the slowest request.
 */
export async function loadPhotos(userIds, onPhoto, concurrency = 6) {
  const queue = [...userIds]

  async function worker() {
    while (queue.length > 0) {
      const id = queue.shift()
      const url = await fetchPhoto(id)
      if (url) onPhoto(id, url)
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, worker)
  )
}

export function initials(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
